import { useQuery } from "@tanstack/react-query";
import { Database, PackageSearch, RefreshCw, Tag, Lock } from "lucide-react";
import { api, type Status } from "@/lib/api";
import { cn } from "@/lib/cn";

// Data-freshness strip. Answers "how old is what I'm looking at?" without anyone having to ask:
//   • ราคา — which master issue is live and when it was applied (app_meta)
//   • สต๊อกตามคลัง — loc_inventory, the AP on-hand behind พร้อมขาย (AP)   · bbt-rop cron, ทุก 3 ชม.
//   • ยอดจอง — open_sales_lines, the reservations subtracted from it      · bbt-rop cron, ทุก 15 นาที
// Colour is judged against each feed's own cadence, so "green" means "as fresh as it is designed to
// get", not "recent in absolute terms". Company on-hand has no queryable stamp in bbt-rop, so its
// hourly cadence is stated in the tooltip rather than shown as a time.

const MIN = 60_000;

type Level = "ok" | "warn" | "bad" | "unknown";

function level(ageMs: number | null, okUntil: number, warnUntil: number): Level {
  if (ageMs == null) return "unknown";
  if (ageMs <= okUntil) return "ok";
  if (ageMs <= warnUntil) return "warn";
  return "bad";
}

const DOT: Record<Level, string> = {
  ok: "bg-emerald-500",
  warn: "bg-[#EF781A]",
  bad: "bg-[#B91C1C]",
  unknown: "bg-muted-foreground/40",
};

function ago(ms: number | null, now: number): string {
  if (ms == null) return "ไม่มีข้อมูล";
  const m = Math.max(0, Math.round((now - ms) / MIN));
  if (m < 1) return "เมื่อสักครู่";
  if (m < 60) return `${m} นาทีที่แล้ว`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ชม.${m % 60 ? ` ${m % 60} น.` : ""}ที่แล้ว`;
  return `${Math.floor(h / 24)} วันที่แล้ว`;
}

function clock(ms: number | null): string {
  if (ms == null) return "—";
  return new Date(ms).toLocaleString("th-TH", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function SyncStatus() {
  const q = useQuery({
    queryKey: ["status"],
    queryFn: () => api.status(),
    refetchInterval: 2 * MIN,
    staleTime: MIN,
  });
  const s: Status | undefined = q.data;
  // Clock-skew guard: age is measured against the worker's clock, not the browser's.
  const now = s?.now ?? Date.now();

  if (!s) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <Database className="h-3.5 w-3.5 animate-pulse" /> กำลังอ่านสถานะข้อมูล…
      </div>
    );
  }

  const locAge = s.stock.locAt == null ? null : now - s.stock.locAt;
  const resvAge = s.stock.reservationsAt == null ? null : now - s.stock.reservationsAt;

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
      <Chip
        icon={<Tag className="h-3 w-3" />}
        label="ราคา"
        value={s.price.master ?? "ไม่ระบุรุ่น"}
        sub={s.price.appliedAt ? clock(s.price.appliedAt) : undefined}
        tone="brand"
        title={[
          `ไฟล์ต้นฉบับ: ${s.price.sourceFile ?? "—"}`,
          `อัปเดตราคาเมื่อ: ${clock(s.price.appliedAt)} (${ago(s.price.appliedAt, now)})`,
          s.price.appliedBy ? `โดย: ${s.price.appliedBy}` : null,
          s.price.rowsChanged != null ? `แก้ไข ${s.price.rowsChanged.toLocaleString()} รายการในรอบนั้น` : null,
          `รายการในระบบ: ${s.price.items.toLocaleString()}`,
        ].filter(Boolean).join("\n")}
      />
      <Chip
        icon={<PackageSearch className="h-3 w-3" />}
        label="สต๊อกตามคลัง"
        value={ago(s.stock.locAt, now)}
        dot={level(locAge, 4 * 60 * MIN, 9 * 60 * MIN)}
        title={[
          "สต๊อกแยกคลัง (AP/ST/SJ) ที่ใช้คำนวณ พร้อมขาย (AP)",
          `ซิงค์จาก BC365: ${s.stock.cadence.loc} · ล่าสุด ${clock(s.stock.locAt)}`,
          `ยอดคงเหลือรวมบริษัท: ${s.stock.cadence.onHand}`,
          "ต้องการเลขสด ๆ ตอนนี้ → กด 🔄 ที่แถวสินค้า (ดึงตรงจาก BC)",
        ].join("\n")}
      />
      <Chip
        icon={<Lock className="h-3 w-3" />}
        label="ยอดจอง"
        value={ago(s.stock.reservationsAt, now)}
        dot={level(resvAge, 45 * MIN, 180 * MIN)}
        title={[
          "ยอดจองจากใบสั่งขายที่ยังไม่ส่ง (open sales orders)",
          `ซิงค์จาก BC365: ${s.stock.cadence.reservations} · ล่าสุด ${clock(s.stock.reservationsAt)}`,
        ].join("\n")}
      />
      <button
        onClick={() => q.refetch()}
        disabled={q.isFetching}
        title="อ่านสถานะข้อมูลใหม่"
        className="inline-flex h-6 items-center gap-1 rounded-full border border-border bg-background px-2 font-medium text-muted-foreground transition-colors duration-300 hover:border-beijer/40 hover:text-beijer disabled:opacity-50"
      >
        <RefreshCw className={cn("h-3 w-3", q.isFetching && "animate-spin")} />
      </button>
    </div>
  );
}

function Chip({ icon, label, value, sub, dot, tone, title }: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  dot?: Level;
  tone?: "brand";
  title: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        "inline-flex h-6 max-w-full items-center gap-1.5 rounded-full border px-2 tabular-nums transition-colors duration-300",
        tone === "brand"
          ? "border-beijer/30 bg-beijer/5 text-beijer"
          : "border-border bg-background text-muted-foreground",
      )}
    >
      {dot && <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", DOT[dot])} />}
      <span className="shrink-0 opacity-70">{icon}</span>
      {/* Thai labels: no uppercase/letter-spacing — it distorts the glyph clusters. */}
      <span className="shrink-0 font-semibold opacity-70">{label}</span>
      <span className="truncate font-semibold text-foreground">{value}</span>
      {sub && <span className="hidden shrink-0 opacity-60 sm:inline">· {sub}</span>}
    </span>
  );
}
